/**
 * Bounds Pusher-driven upstream fetches per client per rolling window
 * (default 60/60s). Overflow degrades to eventual consistency — see
 * "Pusher → React Query sync" in src/queries/AGENTS.md.
 */
export interface RateLimiter {
  /** Attempt to take a grant. Returns true if allowed, false if over budget. */
  tryAcquire: () => boolean
  /** Grants used in the current rolling window (for logging / tests). */
  count: () => number
  /** Drop all recorded grants (used by tests). */
  reset: () => void
}

export interface RateLimiterOptions {
  /** Max grants per rolling window. Default 60. */
  maxPerWindow?: number
  /** Rolling window length in ms. Default 60_000 (one minute). */
  windowMs?: number
  /** Clock injection for deterministic tests. Default Date.now. */
  now?: () => number
}

export const createRateLimiter = ({
  maxPerWindow = 60,
  windowMs = 60_000,
  now = Date.now,
}: RateLimiterOptions = {}): RateLimiter => {
  // Timestamps of granted acquisitions within the current window, oldest first.
  let grants: number[] = []

  const prune = (t: number): void => {
    const cutoff = t - windowMs
    // grants is append-only-oldest-first, so drop from the front until in-window.
    let i = 0
    while (i < grants.length && grants[i] <= cutoff) i++
    if (i > 0) grants = grants.slice(i)
  }

  return {
    tryAcquire: () => {
      const t = now()
      prune(t)
      if (grants.length >= maxPerWindow) return false
      grants.push(t)
      return true
    },
    count: () => {
      prune(now())
      return grants.length
    },
    reset: () => {
      grants = []
    },
  }
}
